import { useCallback, useState } from "react";
import { matchItems } from "../../../services/hobblivision/matcher";
import fetchMatcherCandidates from "../../../services/hobblivision/fetchMatcherCandidates";

/**
 * ============================================================
 * BUILD MATCHER INPUT
 * ============================================================
 *
 * The parser is allowed to miss structured fields even when
 * the OCR text contains strong evidence.
 *
 * This function promotes obvious identifiers from the OCR text
 * into the matcher input so retrieval and scoring can use the
 * same evidence.
 *
 * Parser-provided values always take precedence.
 * ============================================================
 */

    function buildMatcherInput(parsedResult = {}) {
        const text = String(
            parsedResult.correctedText ||
            parsedResult.normalizedText ||
            parsedResult.combinedText ||
            ""
        );

        const matcherInput = {
            ...parsedResult,
        };

        /**
         * ========================================================
         * NAME / PLAYER EVIDENCE
         * ========================================================
         *
         * Preserve the parser's existing identity evidence.
         *
         * The matcher already understands:
         *
         *   identityCandidates
         *   nameCandidates
         *   player
         *   itemName
         *
         * We should NOT replace those with text extraction.
         *
         * Text extraction is only a fallback when the parser
         * failed to populate any usable identity evidence.
         */

        const hasNameEvidence =
            Boolean(
                matcherInput.player ||
                matcherInput.itemName ||
                (
                    Array.isArray(
                        matcherInput.identityCandidates
                    ) &&
                    matcherInput.identityCandidates.length
                ) ||
                (
                    Array.isArray(
                        matcherInput.nameCandidates
                    ) &&
                    matcherInput.nameCandidates.length
                )
            );

        /**
         * --------------------------------------------------------
         * NAME FALLBACK FROM TEXT
         * --------------------------------------------------------
         *
         * If the parser did not produce a name, attempt to recover
         * obvious identity text from the parser's corrected text.
         *
         * This is intentionally conservative.
         *
         * We remove known structural evidence first so something
         * like:
         *
         *   MICHAEL JORDAN 1989 HOOPS #21
         *
         * becomes:
         *
         *   MICHAEL JORDAN
         *
         * rather than accidentally treating "1989 HOOPS" as part
         * of the player name.
         */

        if (!hasNameEvidence && text) {
            let nameText = text;

            /**
             * Remove year.
             */
            nameText = nameText.replace(
                /\b(18|19|20)\d{2}\b/g,
                " "
            );

            /**
             * Remove card/set number.
             */
            nameText = nameText.replace(
                /#\s*[A-Z0-9-]+/gi,
                " "
            );

            /**
             * Remove common structural card terms.
             *
             * This is deliberately small. We do not want this
             * hook becoming a second parser.
             */
            nameText = nameText.replace(
                /\b(?:BASE|CARD|NO|NUMBER|ROOKIE|RC)\b/gi,
                " "
            );

            nameText = nameText
                .replace(/\s+/g, " ")
                .trim();

            /**
             * Only use reasonably sized text as fallback identity
             * evidence.
             *
             * Very long OCR text is more likely to contain multiple
             * unrelated fragments than a clean player/item name.
             */
            if (
                nameText &&
                nameText.length <= 80
            ) {
                matcherInput.nameCandidates = [
                    {
                        value: nameText,
                        confidence: 0.65,
                        source: "matcherTextFallback",
                    },
                ];

                matcherInput.nameConfidence = 0.65;

                console.log(
                    "[HobbliVision] Matcher promoted text to name evidence:",
                    nameText
                );
            }
        }

        /**
         * ========================================================
         * YEAR
         * ========================================================
         */

        if (!matcherInput.year) {
            const yearMatch = text.match(
                /\b(18|19|20)\d{2}\b/
            );

            if (yearMatch) {
                matcherInput.year =
                    Number(yearMatch[0]);

                matcherInput.yearConfidence =
                    0.95;
            }
        }

        /**
         * ========================================================
         * SET
         * ========================================================
         *
         * Example:
         *
         *   "1989 HOOPS #21"
         *
         * Extracts the text between the year and card number.
         */

        if (!matcherInput.set) {
            const setMatch = text.match(
                /\b(?:18|19|20)\d{2}\s+([A-Z][A-Z0-9'&.-]*(?:\s+[A-Z0-9'&.-]+){0,3})\s+#\s*[A-Z0-9-]+/i
            );

            if (setMatch) {
                matcherInput.set =
                    setMatch[1].trim();

                matcherInput.setConfidence =
                    0.90;
            }
        }

        /**
         * ========================================================
         * CARD NUMBER
         * ========================================================
         */

        if (!matcherInput.cardNumber) {
            const numberMatch = text.match(
                /#\s*([A-Z0-9-]+)/i
            );

            if (numberMatch) {
                matcherInput.cardNumber =
                    numberMatch[1].trim();

                matcherInput.cardNumberConfidence =
                    0.90;
            }
        }

        return matcherInput;
    }



/**
 * ============================================================
 * HOBBLIVISION MATCHER HOOK
 * ============================================================
 *
 * Responsibilities:
 *
 *   1. Receive parser output
 *   2. Normalize/promote matcher evidence
 *   3. Obtain candidate records
 *   4. Pass ALL candidates to matcher.js
 *   5. Return ranked matcher results
 *
 * Candidate retrieval and candidate scoring remain separate:
 *
 *   fetchMatcherCandidates()
 *          ↓
 *      search universe
 *          ↓
 *      matchItems()
 *          ↓
 *      ranked matches
 *
 * matcher.js remains database-agnostic.
 * ============================================================
 */

export default function useMatcher() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const match = useCallback(
        async (
            parsedResult,
            candidateItems = [],
            userId = null
        ) => {
            const started = performance.now();

            try {
                setLoading(true);
                setError(null);

                console.log(
                    "HobbliVision Matcher: starting match..."
                );

                /**
                 * ------------------------------------------------
                 * BUILD MATCHER INPUT
                 * ------------------------------------------------
                 */

                const matcherInput =
                    buildMatcherInput(parsedResult);

                console.log(
                    "[HobbliVision] Matcher input:",
                    matcherInput
                );

                /**
                 * ------------------------------------------------
                 * CANDIDATE ACQUISITION
                 * ------------------------------------------------
                 *
                 * If the caller supplies candidates, use them.
                 *
                 * Otherwise retrieve the complete candidate universe
                 * from canonical items + user_items.
                 *
                 * IMPORTANT:
                 *
                 * We do NOT truncate candidates here.
                 *
                 * matchItems() is responsible for scoring and
                 * selecting the best results.
                 * ------------------------------------------------
                 */

                let candidates = candidateItems;
                let candidateSource = "caller";

                let canonicalCandidates = [];
                let userItemCandidates = [];
                let nameEvidence = null;
                let resolvedNameEvidence = null;

                if (!Array.isArray(candidates)) {
                    candidates = [];
                }

                if (candidates.length === 0) {
                    console.log(
                        "HobbliVision Matcher: fetching database candidates..."
                    );

                    const retrieved =
                        await fetchMatcherCandidates(
                            matcherInput,
                            userId
                        );

                    candidates =
                        Array.isArray(retrieved?.candidates)
                            ? retrieved.candidates
                            : [];

                    canonicalCandidates =
                        Array.isArray(
                            retrieved?.canonicalCandidates
                        )
                            ? retrieved.canonicalCandidates
                            : [];

                    userItemCandidates =
                        Array.isArray(
                            retrieved?.userItemCandidates
                        )
                            ? retrieved.userItemCandidates
                            : [];

                    nameEvidence = retrieved?.nameEvidence || null;
                    resolvedNameEvidence = retrieved?.resolvedNameEvidence || null;
                    candidateSource = "database";

                    console.log(
                        "[HobbliVision] Matcher candidate counts:",
                        {
                            canonical:
                                canonicalCandidates.length,

                            userItems:
                                userItemCandidates.length,

                            combined:
                                candidates.length,
                        }
                    );
                }

                console.log(
                    "[HobbliVision] Candidates entering matcher:",
                    candidates.length
                );

                /**
                 * ------------------------------------------------
                 * CANDIDATE SAMPLE
                 * ------------------------------------------------
                 *
                 * Diagnostic only.
                 *
                 * This intentionally shows a small sample while
                 * the FULL candidates array is passed below.
                 */

                console.log(
                    "[HobbliVision] Candidate sample:",
                    candidates.slice(0, 10).map((item) => ({
                        id: item.id,

                        name:
                            item.name ??
                            item.user_specific_name ??
                            item.itemName,

                        set:
                            item.set ??
                            item.set_name ??
                            item.user_specific_set_name,

                        year:
                            item.year ??
                            item.user_specific_year,

                        cardNumber:
                            item.cardNumber ??
                            item.card_number,

                        source:
                            item.source ??
                            item.sourceType,
                    }))
                );

                /**
                 * ------------------------------------------------
                 * MATCH
                 * ------------------------------------------------
                 *
                 * Pass the COMPLETE candidate array.
                 *
                 * matchItems() is responsible for:
                 *
                 *   - normalization
                 *   - scoring
                 *   - relationship evidence
                 *   - contradictions
                 *   - identifier bonuses
                 *   - ranking
                 *   - MAX_CANDIDATES output limiting
                 */

                const result =
                    matchItems(
                        matcherInput,
                        candidates
                    );

                /**
                 * ------------------------------------------------
                 * MATCH RESULTS
                 * ------------------------------------------------
                 */

                if (Array.isArray(result?.candidates)) {
                    console.table(
                        result.candidates.map((candidate) => ({
                            id: candidate.id,

                            itemName:
                                candidate.itemName ??
                                candidate.name,

                            source:
                                candidate.source ??
                                candidate.sourceType,

                            score:
                                candidate.score ?? 0,

                            nameScore:
                                candidate.scoreBreakdown
                                    ?.directEvidence
                                    ?.name
                                    ?.score ?? 0,

                            setScore:
                                candidate.scoreBreakdown
                                    ?.directEvidence
                                    ?.set
                                    ?.score ?? 0,

                            yearScore:
                                candidate.scoreBreakdown
                                    ?.directEvidence
                                    ?.year
                                    ?.score ?? 0,

                            cardNumberScore:
                                candidate.scoreBreakdown
                                    ?.directEvidence
                                    ?.cardNumber
                                    ?.score ?? 0,

                            baseScore:
                                candidate.scoreBreakdown
                                    ?.baseScore ?? 0,

                            relationshipScore:
                                candidate.scoreBreakdown
                                    ?.relationships
                                    ?.total ?? 0,

                            contradictionPenalty:
                                candidate.scoreBreakdown
                                    ?.contradictions
                                    ?.total ?? 0,

                            identifierBonus:
                                candidate.scoreBreakdown
                                    ?.identifierBonus ?? 0,
                        }))
                    );
                }

                /**
                 * ------------------------------------------------
                 * DIAGNOSTICS
                 * ------------------------------------------------
                 */

                const elapsed = (
                    performance.now() - started
                ).toFixed(0);

                const diagnostics = {
                    ...(result?.diagnostics || {}),

                    candidateSource,

                    retrievedCandidateCount:
                        candidates.length,

                    canonicalCandidateCount:
                        canonicalCandidates.length,

                    userItemCandidateCount:
                        userItemCandidates.length,

                    nameEvidence,

                    processingMs:
                        Number(elapsed),
                };

                const finalResult = {
                    ...result,
                    resolvedNameEvidence,
                    diagnostics,
                };

                console.log(
                    `HobbliVision Matcher: completed after ${elapsed}ms.`,
                    finalResult
                );

                return finalResult;

            } catch (err) {
                console.error(
                    "HobbliVision Matcher ERROR:",
                    err
                );

                setError(err);

                return {
                    version: "2.0",
                    matched: false,
                    bestMatch: null,
                    candidates: [],
                    confidence: 0,

                    diagnostics: {
                        reason:
                            err?.message ||
                            "Unknown matcher error",
                    },
                };

            } finally {
                setLoading(false);
            }
        },
        []
    );

    return {
        match,
        loading,
        error,
    };
}

